<template>
  <div class="p-2 sm:p-6 lg:p-8 max-w-8xl mx-auto space-y-2">
    
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-4 border-b border-gray-200 dark:border-gray-700 gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">New Employment Contract</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Create and manage employment contracts for staff members.</p>
      </div>
      <button 
        @click="$emit('back')" 
        class="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Dashboard
      </button>
    </div>

    <div class="space-y-8">
      <!-- SECTION 1: Employee & Basic Contract Info -->
      <div class="bg-white border border-black-800 dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Employee & Contract Basics
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- Employee Select -->
          <div class="lg:col-span-1">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Employee *</label>
            <Multiselect
              v-model="selectedEmployeeModel"
              :options="filteredApplicants"
              :custom-label="app => app.name"
              track-by="id"
              searchable
              placeholder="Search employee..."
              :show-labels="false"
              class="multiselect-custom"
            />
          </div>

          <!-- Employee Code -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee Code</label>
            <input 
              :value="generatedEmployeeCode || 'N/A'" 
              readonly
              class="mt-1 block w-full rounded-lg border border-black-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 shadow-sm py-2.5 px-3 bg-gray-50"
              :class="{'border-green-500 ring-1 ring-green-500 dark:ring-green-500': hasAcceptedDraft}"
            >
          </div>

          <!-- Contract Type -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contract Type *</label>
            <select v-model="contract.type" required
                    class="mt-1 block w-full rounded-lg border border-black-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              <option disabled value="">Select type</option>
              <option v-for="type in contracts" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>

          <!-- Duration -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Duration (months) *</label>
            <input v-model.number="contract.duration" type="number" min="1" required
                   class="mt-1 block w-full rounded-lg border border-black-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
          </div>
        </div>

        <div class="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- Start Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date *</label>
            <input v-model="contract.startDate" type="date" required
                   class="mt-1 block w-full rounded-lg border border-black-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
          </div>

          <!-- End Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
            <input v-model="contract.endDate" type="date"
                   :readonly="contract.autoCalculateEndDate"
                   class="mt-1 block w-full rounded-lg border border-black-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3"
                   :class="{'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': contract.autoCalculateEndDate}">
            
            <div class="mt-3 flex items-center">
              <input id="auto-calculate" v-model="contract.autoCalculateEndDate" type="checkbox"
                     class="h-4 w-4 rounded border-gray-300 text-[#035a76] focus:ring-[#035a76]">
              <label for="auto-calculate" class="ml-2 block text-sm text-gray-600 dark:text-gray-400">
                Auto-calculate end date
              </label>
            </div>
          </div>

          <!-- Language -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Language of Support</label>
            <select v-model="contract.language"
                    class="mt-1 block w-full rounded-lg border border-black-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              <option disabled value="">Select</option>
              <option value="French">French</option>
              <option value="English">English</option>
            </select>
          </div>

          <!-- Report To -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Report To *</label>
            <select v-model="contract.report" required
                    class="mt-1 block w-full rounded-lg border border-black-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              <option disabled value="">Select Position</option>
              <option v-for="position in positions" :key="position.id" :value="position.id">{{ position.name }}</option>
            </select>
          </div>
        </div>

        <!-- Prefill Status -->
        <div v-if="hasAcceptedDraft && !isPrefilling" class="mt-6 flex items-center p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <svg class="h-5 w-5 text-green-600 dark:text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-sm font-medium text-green-800 dark:text-green-300">Form pre-filled from accepted contract draft</span>
        </div>
        
        <div v-if="isPrefilling" class="mt-6 flex items-center text-sm text-gray-500 dark:text-gray-400">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-[#035a76]" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading previous contract details...
        </div>
      </div>

      <!-- SECTION 2: Position Details -->
      <div class="bg-white border border-black-800 dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Position Details
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department *</label>
            <select v-model="contract.department" required
                    class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              <option disabled value="">Select department</option>
              <option v-for="department in departments" :key="department.id" :value="department.id">{{ department.name }}</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Job Title / Position *</label>
            <select v-model="contract.position" required
                    class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              <option disabled value="">Select Position</option>
              <option v-for="position in positions" :key="position.id" :value="position.id">{{ position.name }}</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Probation Period</label>
            <select v-model="contract.probationPeriod"
                    class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              <option value="0">No probation</option>
              <option value="1">1 month</option>
              <option value="3">3 months</option>
              <option value="6">6 months</option>
              <option value="12">12 months</option>
            </select>
          </div>
        </div>

        <!-- Teaching Specific Details -->
        <div v-if="[1, 6, 14].includes(Number(contract.department))" class="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 class="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <svg class="w-4 h-4 text-[#76ba51]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Teaching Specific Details
          </h3>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Section *</label>
              <select v-model="contract.section" required
                      class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
                <option disabled value="">Select section</option>
                <option v-for="section in sections" :key="section.id" :value="section.id">{{ section.name }}</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Class Assignment *</label>
              <select v-model="contract.class" required
                      class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
                <option value="">Select class</option>
                <option v-for="item in filteredClasses" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
            </div>

            <div class="lg:col-span-1">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subjects</label>
              <div class="space-y-3 mt-1">
                <div v-for="(subjectId, index) in contract.subjects" :key="index" class="flex items-center gap-2">
                  <select 
                    v-model="contract.subjects[index]" 
                    class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2 px-3">
                    <option disabled value="">Select a subject</option>
                    <option v-for="subj in subjects" :key="subj.id" :value="subj.id">
                      {{ subj.name }} {{ subj.code ? `(${subj.code})` : '' }}
                    </option>
                  </select>

                  <button 
                    v-if="contract.subjects.length > 1" 
                    @click="removeSubject(index)" 
                    type="button"
                    class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Remove subject"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <button 
                  @click="addSubject" 
                  type="button"
                  class="inline-flex items-center px-3 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-[#035a76] dark:text-blue-400 bg-transparent hover:bg-[#035a76]/5 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                  Add Subject
                </button>

                <div v-if="selectedSubjectNames" class="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-700">
                  <span class="font-medium">Selected:</span> {{ selectedSubjectNames }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 3: Salary, Benefits & Termination -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Salary & Benefits -->
        <div class="bg-white border border-black-600 dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Salary & Benefits
          </h2>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div v-if="hasPermission('contracts', 'add_salary')">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Base Salary (GHS) *</label>
              <input v-model.number="contract.salary" type="number" min="0" required
                     class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Frequency *</label>
              <select v-model="contract.paymentFrequency" required
                      class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
                <option value="monthly">Monthly</option>
                <option value="bi-weekly">Bi-Weekly</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Leave Days *</label>
              <input v-model.number="contract.leaveDays" type="number" min="0" required
                     class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SSNIT Contribution</label>
              <select v-model="contract.ssnit"
                      class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
                <option disabled value="">Select option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PAYE</label>
              <select v-model="contract.paye"
                      class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
                <option disabled value="">Select option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medical Entitlement</label>
              <select v-model="contract.medical"
                      class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
                <option disabled value="">Select option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div class="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700" v-if="hasPermission('contracts', 'add_benefit')">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Additional Benefits</label>
            <select v-model="contract.hasBenefits"
                    class="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              <option disabled value="">Select benefits</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          
            <div v-if="contract.hasBenefits === 'Yes'" class="mt-4 space-y-3">
              <div v-for="(benefit, index) in contract.benefits" :key="index" class="flex items-center gap-3">
                <select v-model="benefit.type" class="flex-1 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm py-2 px-3">
                  <option disabled value="">Select benefit</option>
                  <option v-for="b in benefits" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>

                <input type="number" v-model="benefit.amount" placeholder="Amount"
                       class="w-32 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm py-2 px-3">

                <button v-if="contract.benefits.length > 1" @click="removeBenefit(index)" type="button"
                        class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <button @click="addBenefit" type="button"
                      class="inline-flex items-center px-3 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-[#035a76] dark:text-blue-400 bg-transparent hover:bg-[#035a76]/5 dark:hover:bg-blue-900/20 transition-colors">
                <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                Add Benefit
              </button>
              
              <div v-if="selectedBenefitNames" class="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-700">
                <span class="font-medium">Selected:</span> {{ selectedBenefitNames }}
              </div>
            </div>
          </div>
        </div>

        <!-- Termination Clause -->
        <div class="bg-white border border-black-600 dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Termination Clause
          </h2>
          
          <div class="flex items-center mb-4">
            <input id="include-termination" v-model="contract.includeTermination" type="checkbox"
                   class="h-4 w-4 rounded border-gray-300 text-[#035a76] focus:ring-[#035a76]">
            <label for="include-termination" class="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Include termination clause
            </label>
          </div>
          
          <div v-if="contract.includeTermination" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notice Period (days) *</label>
                <input v-model.number="contract.noticePeriod" type="number" min="1"
                       class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Termination Reason Required</label>
                <select v-model="contract.terminationReasonRequired"
                        class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3">
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Termination Conditions</label>
              <textarea v-model="contract.terminationConditions" rows="4"
                        class="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-[#035a76] focus:ring-[#035a76] py-2.5 px-3 resize-none"></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Footer (Sticky) -->
      <div class="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-end gap-3 z-10">
        <button type="button" @click="resetForm"
                class="inline-flex justify-center items-center px-5 py-2.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76] dark:focus:ring-offset-gray-900 transition-colors">
          Reset Form
        </button>
        
        <button v-if="existingDraft?.id" type="button" @click="updateContract" :disabled="isSaving"
                class="inline-flex justify-center items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 transition-colors">
          <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isSaving ? 'Updating...' : 'Update Draft' }}
        </button>

        <button type="button" @click="saveContract" :disabled="isSaving || hasAcceptedDraft"
                class="inline-flex justify-center items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 dark:focus:ring-offset-gray-900 transition-colors">
          <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isSaving ? 'Saving...' : 'Generate Draft' }}
        </button>

        <button type="button" @click="generateContract" :disabled="isSaving || !hasAcceptedDraft"
                class="inline-flex justify-center items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#76ba51] hover:bg-[#66a345] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#76ba51] dark:focus:ring-offset-gray-900 transition-colors">
          <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isSaving ? 'Generating...' : 'Save Contract' }}
        </button>
      </div>
    </div>

    <!-- Hidden PDF Template Container -->
    <div id="pdf-template" style="display: none; width: 800px; padding: 40px; background: white; font-family: 'Times New Roman', serif;"></div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import api from "@/services/api";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { usePermissions } from '@/composables/usePermissions';

const { hasPermission } = usePermissions();

const emit = defineEmits(['back', 'submit']);

const applicants = ref([]);
const contracts = ref([]);
const departments = ref([]);
const positions = ref([]);
const sections = ref([]);
const classes = ref([]);
const subjects = ref([]);
const benefits = ref([]);

const generatedEmployeeCode = ref('');
const isPrefilling = ref(false);
const hasAcceptedDraft = ref(false);
const isSaving = ref(false);
const existingDraft = ref(null);

const selectedSubjectNames = computed(() => {
  if (!contract.value.subjects) return "";
  return contract.value.subjects
    .map(id => {
      const subject = subjects.value.find(s => String(s.id) === String(id));
      return subject ? subject.name : null;
    })
    .filter(Boolean)
    .join(", ");
});

const selectedBenefitNames = computed(() => {
  if (!contract.value.benefits) return "";
  return contract.value.benefits
    .map(b => {
      const benefit = benefits.value.find(x => String(x.id) === String(b.type));
      return benefit ? `${benefit.name} (${b.amount})` : null;
    })
    .filter(Boolean)
    .join(", ");
});

const selectedEmployeeModel = computed({
  get() {
    if (!contract.value.employee) return null;
    return filteredApplicants.value.find(app => String(app.id) === String(contract.value.employee)) || null;
  },
  set(newValue) {
    contract.value.employee = newValue ? newValue.id : '';
  }
});

const filteredApplicants = computed(() => {
  return applicants.value.filter(applicant =>
    applicant.status?.toLowerCase() === "validated" ||
    applicant.status?.toLowerCase() === "contracting"
  );
});

const filteredClasses = computed(() => {
  if (!contract.value.section) return [];
  const selectedSectionId = contract.value.section; 
  return classes.value.filter(cls => String(cls.section_id) === String(selectedSectionId));
});

const contract = ref({
  employee: '',
  type: '',
  duration: 0,
  probationPeriod: '3',
  startDate: '',
  endDate: '',
  language: '',
  report: '',
  autoCalculateEndDate: true,
  department: '',
  position: '',
  section: '',
  class: '',
  subjects: [''],
  salary: 0,
  paymentFrequency: 'monthly',
  leaveDays: 0,
  ssnit: '',
  paye: '',
  medical: '',
  hasBenefits: 'No',
  benefits: [{ type: '', amount: '' }],
  includeTermination: true,
  noticePeriod: 30,
  terminationReasonRequired: 'Yes',
  terminationConditions: 'Either party may terminate this contract by providing written notice as specified in the notice period section.'
});

// Watch for changes to auto-calculate end date
watch(() => [contract.value.startDate, contract.value.duration, contract.value.autoCalculateEndDate], 
  ([startDate, duration, autoCalculate]) => {
    if (autoCalculate && startDate && duration) {
      const start = new Date(startDate);
      start.setMonth(start.getMonth() + parseInt(duration));
      contract.value.endDate = start.toISOString().split('T')[0];
    }
  },
  { deep: true }
);

watch(() => contract.value.section, () => {
  contract.value.class = "";
});

watch(() => contract.value.hasBenefits, (val) => {
  if (val === 'Yes' && (!Array.isArray(contract.value.benefits) || contract.value.benefits.length === 0)) {
    contract.value.benefits = [{ type: '', amount: '' }];
  }
});

watch(() => contract.value.employee, async (newApplicantId) => {
  if (!newApplicantId) {
    resetFormFields();
    generatedEmployeeCode.value = '';
    hasAcceptedDraft.value = false;
    existingDraft.value = null;
    return;
  }
  await checkAndPrefillFromDraft(newApplicantId);
});

const fetchApplicants = async () => {
  try {
    const response = await api.get("Applicants.php");
    applicants.value = response.data.map(applicant => ({
      id: applicant.id,
      name: applicant.full_name,
      position: applicant.position_title,
      status: applicant.applicant_status,
    }));
  } catch (error) {
    console.error("Error fetching Applicants:", error);
  }
};

const fetchContractType = async () => {
  try {
    const response = await api.get("ContractType.php");
    contracts.value = response.data.map(contract => ({
      id: contract.id,
      name: contract.name,
      code: contract.code,
      description: contract.description,
      duration: contract.duration
    }));
  } catch (error) {
    console.error("Error fetching Contract Types:", error);
  }
};

const fetchDepartments = async () => {
  try {
    const response = await api.get("Departments.php");
    departments.value = response.data.map(department => ({
      id: department.id,
      name: department.name,
      code: department.code,
      description: department.description
    }));
  } catch (error) {
    console.error("Error fetching Departments:", error);
  }
};

const fetchPositions = async () => {
  try {
    const response = await api.get("Positions.php");
    positions.value = response.data.map(position => ({
      id: position.id,
      department_id: position.department_id,
      department_name: position.department_name,
      name: position.title,
      code: position.code,
      description: position.description
    }));
  } catch (error) {
    console.error("Error fetching Positions:", error);
  }
};

const fetchSections = async () => {
  try {
    const response = await api.get("Sections.php");
    sections.value = response.data.map(section => ({
      id: section.id,
      name: section.name,
      code: section.code,
      description: section.description
    }));
  } catch (error) {
    console.error("Error fetching Sections:", error);
  }
};

const fetchClasses = async () => {
  try {
    const response = await api.get("Classes.php");
    classes.value = response.data.map(item => ({
      id: item.id,
      section_id: item.section_id,
      section_name: item.section_name,
      name: item.name,
      code: item.code,
      description: item.description
    }));
  } catch (error) {
    console.error("Error fetching Classes:", error);
  }
};

const fetchSubjects = async () => {
  try {
    const response = await api.get("Subjects.php");
    subjects.value = response.data.map(subject => ({
      id: subject.id,
      name: subject.name,
      code: subject.code,
      description: subject.description
    }));
  } catch (error) {
    console.error("Error fetching Subjects:", error);
  }
};

const fetchBenefits = async () => {
  try {
    const response = await api.get("Benefits.php");
    benefits.value = response.data.map(benefit => ({
      id: benefit.id,
      name: benefit.name,
      code: benefit.code,
      description: benefit.description
    }));
  } catch (error) {
    console.error("Error fetching Benefits:", error);
  }
};

const addSubject = () => { contract.value.subjects.push(''); };
const removeSubject = (index) => { contract.value.subjects.splice(index, 1); };
const addBenefit = () => { contract.value.benefits.push({ type: '', amount: '' }); };
const removeBenefit = (index) => { contract.value.benefits.splice(index, 1); };

const resetForm = () => {
  contract.value = {
    employee: '',
    type: '',
    duration: 0,
    probationPeriod: '3',
    startDate: '',
    endDate: '',
    language: '',
    report: '',
    autoCalculateEndDate: true,
    department: '',
    position: '',
    section: '',
    class: '',
    subjects: [''],
    salary: 0,
    paymentFrequency: 'monthly',
    leaveDays: 0,
    ssnit: '',
    paye: '',
    medical: '',
    hasBenefits: 'No',
    benefits: [{ type: '', amount: '' }],
    includeTermination: true,
    noticePeriod: 30,
    terminationReasonRequired: 'Yes',
    terminationConditions: 'Either party may terminate this contract by providing written notice as specified in the notice period section.'
  };
  generatedEmployeeCode.value = '';
  hasAcceptedDraft.value = false;
  existingDraft.value = null;
};

const resetFormFields = () => {
  contract.value = {
    ...contract.value,
    type: '',
    duration: 0,
    probationPeriod: '3',
    startDate: '',
    endDate: '',
    language: '',
    autoCalculateEndDate: false,
    department: '',
    position: '',
    section: '',
    class: '',
    subjects: [''],
    salary: 0,
    paymentFrequency: 'monthly',
    leaveDays: 0,
    ssnit: 'No',
    paye: '',
    medical: '',
    hasBenefits: 'No',
    benefits: [{ type: '', amount: '' }],
    includeTermination: true,
    noticePeriod: 30,
    terminationReasonRequired: 'No',
    terminationConditions: ''
  };
  generatedEmployeeCode.value = '';
  hasAcceptedDraft.value = false;
  existingDraft.value = null;
};

const saveContract = async () => {
  if (!contract.value.employee) { alert('Please select an employee'); return; }
  if (!contract.value.type) { alert('Please select contract type'); return; }
  if (!contract.value.startDate) { alert('Please select start date'); return; }
  
  try {
    isSaving.value = true;
    const payload = {
      employee: contract.value.employee,
      type: contract.value.type,
      duration: contract.value.duration,
      probationPeriod: contract.value.probationPeriod,
      startDate: contract.value.startDate,
      endDate: contract.value.endDate,
      language: contract.value.language,
      report: contract.value.report,
      department: contract.value.department,
      position: contract.value.position,
      section: contract.value.section || null,
      class: contract.value.class || null,
      subjects: contract.value.subjects.filter(s => s !== ''),
      salary: contract.value.salary,
      paymentFrequency: contract.value.paymentFrequency,
      leaveDays: contract.value.leaveDays,
      ssnit: contract.value.ssnit,
      paye: contract.value.paye,
      medical: contract.value.medical,
      hasBenefits: contract.value.hasBenefits,
      benefits: contract.value.benefits,
      noticePeriod: contract.value.noticePeriod,
      terminationReasonRequired: contract.value.terminationReasonRequired,
      terminationConditions: contract.value.terminationConditions
    };
    
    const response = await api.post('SaveContract.php', payload);
    if (response.data.success) {
      alert('✅ Contract saved! Ref: ' + response.data.contract_number);
      resetForm();
    } else {
      alert('❌ Error: ' + response.data.error);
    }
  } catch (error) {
    console.error(error);
    alert('❌ Failed to save contract');
  } finally {
    isSaving.value = false;
  }
};

const generateContract = async () => {
  if (!contract.value.employee) { alert('Please select an employee'); return; }
  if (!hasAcceptedDraft.value) { alert('No accepted draft found. Please save and submit a contract first.'); return; }
  if (!generatedEmployeeCode.value) { alert('Employee code not generated. Please refresh and try again.'); return; }
  if (!confirm('Generate contract for ' + (selectedEmployeeModel.value?.name || 'this employee') + '?')) { return; }
  
  try {
    isSaving.value = true;
    const draftId = existingDraft.value?.id || 0;
    const payload = {
      applicant_id: contract.value.employee,
      draft_id: draftId,
      employee_code: generatedEmployeeCode.value,
      basic_salary: contract.value.salary || null,
      payment_frequency: contract.value.paymentFrequency || null,
      leaveDays: contract.value.leaveDays || null,
      hasBenefits: contract.value.hasBenefits || null,
      ssnit: contract.value.ssnit || null,
      paye: contract.value.paye || null,
      medical: contract.value.medical || null,
      benefits: contract.value.hasBenefits === 'Yes' ? contract.value.benefits.filter(b => b.type && b.amount) : []
    };
    
    const response = await api.post('FinalizeContract.php', payload);
    if (response.data.success) {
      alert('✅ Contract generated successfully!\nEmployee Code: ' + generatedEmployeeCode.value);
      resetForm();
      emit('submit', { contractId: response.data.contract_id, employeeCode: generatedEmployeeCode.value });
    } else {
      throw new Error(response.data.error || 'Failed to generate contract');
    }
  } catch (error) {
    console.error('Generate contract error:', error);
    alert('❌ Error: ' + error.message);
  } finally {
    isSaving.value = false;
  }
};

const updateContract = async () => {
  try {
    isSaving.value = true;
    const formatDate = (val) => {
      if (!val) return null;
      if (typeof val === 'string' && val.length === 4) return `${val}-01-01`;
      return new Date(val).toISOString().split('T')[0];
    };

    const payload = {
      draft_id: existingDraft.value.id,
      employee: contract.value.employee,
      type: contract.value.type,
      duration: contract.value.duration,
      probationPeriod: contract.value.probationPeriod,
      startDate: formatDate(contract.value.startDate),
      endDate: formatDate(contract.value.endDate),
      language: contract.value.language,
      report: contract.value.report,
      department: contract.value.department,
      position: contract.value.position,
      section: contract.value.section,
      class: contract.value.class,
      subjects: contract.value.subjects.filter(s => s),
      salary: contract.value.salary,
      paymentFrequency: contract.value.paymentFrequency,
      leaveDays: contract.value.leaveDays,
      ssnit: contract.value.ssnit,
      paye: contract.value.paye,
      medical: contract.value.medical,
      hasBenefits: contract.value.hasBenefits,
      benefits: contract.value.benefits,
      noticePeriod: contract.value.noticePeriod,
      terminationReasonRequired: contract.value.terminationReasonRequired,
      terminationConditions: contract.value.terminationConditions
    };

    const response = await api.post('UpdateContractDraft.php', payload);
    if (response.data.success) {
      alert('✅ Draft updated successfully');
    } else {
      alert(response.data.error || 'Failed to update draft');
    }
  } catch (error) {
    console.error(error);
    alert('Failed to update draft');
  } finally {
    isSaving.value = false;
  }
};

const checkAndPrefillFromDraft = async (applicantId) => {
  try {
    isPrefilling.value = true;
    hasAcceptedDraft.value = false;
    const response = await api.post('GenerateEmployeeCode.php', { applicant_id: applicantId });
    
    if (response.data.success && response.data.draft) {
      hasAcceptedDraft.value = true;
      generatedEmployeeCode.value = response.data.employee_code;
      prefillFormFromDraft(response.data.draft);
    } else {
      resetFormFields();
      generatedEmployeeCode.value = '';
      hasAcceptedDraft.value = false;
    }
  } catch (error) {
    console.error('Error checking draft:', error);
  } finally {
    isPrefilling.value = false;
  }
};

const prefillFormFromDraft = (draft) => {
  existingDraft.value = draft;
  const prefilledBenefits = Array.isArray(draft.benefits) && draft.benefits.length > 0
    ? draft.benefits.map(b => ({ type: String(b.type), amount: b.amount }))
    : [{ type: '', amount: '' }];

  contract.value = {
    ...contract.value,
    employee: draft.applicant_id,
    type: draft.contract_type_id || '',
    duration: draft.duration || '0',
    probationPeriod: draft.probation_period,
    startDate: draft.start_date || '',
    endDate: draft.end_date || '',
    language: draft.language || '',
    report: draft.report_to || '',
    department: draft.department_id || '',
    position: draft.position_id || '',
    section: draft.section_id || '',
    class: draft.class_id || '',
    subjects: draft.subjects?.length ? draft.subjects.map(s => String(s.id)) : [''],
    salary: draft.basic_salary || 0,
    paymentFrequency: draft.payment_frequency || 'monthly',
    leaveDays: draft.leave_days || 0,
    ssnit: draft.ssnit || 'No',
    paye: draft.paye || 'No',
    medical: draft.medical || '',
    hasBenefits: draft.hasBenefits || 'No',
    benefits: prefilledBenefits,
    includeTermination: draft.termination_required === 'Yes',
    noticePeriod: draft.notice_period_days || 30,
    terminationReasonRequired: draft.termination_required || 'No',
    terminationConditions: draft.termination_condition || ''
  };
};

onMounted(() => {
  fetchApplicants();
  fetchContractType();
  fetchDepartments();
  fetchPositions();
  fetchSections();
  fetchClasses();
  fetchSubjects();
  fetchBenefits();
});
</script>

<style scoped>
/* Custom Dark Mode Styling for Vue-Multiselect */
:deep(.multiselect__tags) {
  @apply bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg min-h-[42px] py-2 px-3;
}
:deep(.multiselect__input) {
  @apply bg-transparent dark:bg-gray-700 text-gray-900 dark:text-white;
}
:deep(.multiselect__content-wrapper) {
  @apply bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600;
}
:deep(.multiselect__option) {
  @apply text-gray-900 dark:text-white;
}
:deep(.multiselect__option--highlight) {
  @apply bg-[#035a76] text-white;
}
:deep(.multiselect__option--selected) {
  @apply bg-[#035a76]/10 text-[#035a76] dark:bg-[#035a76]/30 dark:text-blue-300;
}
:deep(.multiselect__placeholder) {
  @apply text-gray-400 dark:text-gray-500;
}

</style>